import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Divider, FormControlLabel, ListSubheader, MenuItem, MenuList, Popover, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from '@mui/material/styles';
import groupTargetMedias from './groupTargetMedias';

function LetterSubheader(props) {
    return <ListSubheader {...props} role="presentation" />;
}
LetterSubheader.muiSkipListHighlight = true;

export default function PublicationSelection({
    open = true,
    effectiveTargetMedia,
    targetMediaOptions = [],
    onTargetMediaChange,
    effectiveMultiPublicationTargetMedias = [],
    availableMultiPublicationTargetMedias = [],
    canEditFields,
    isPending,
    setArrayField,
    editableFields,
    getTargetMediaLabel,
}) {
    const options = Array.from(new Set([
        ...availableMultiPublicationTargetMedias,
        ...effectiveMultiPublicationTargetMedias,
    ])).filter(media => media !== effectiveTargetMedia);
    const selected = effectiveMultiPublicationTargetMedias.filter(media => options.includes(media));
    const hasSelected = selected.length > 0;
    const [expanded, setExpanded] = React.useState(hasSelected);
    const wasOpen = React.useRef(false);
    const [addMediaAnchor, setAddMediaAnchor] = React.useState(null);
    const [mediaSearch, setMediaSearch] = React.useState('');

    React.useEffect(() => {
        if (!open || !expanded || !canEditFields || isPending) {
            setAddMediaAnchor(null);
            setMediaSearch('');
        }
    }, [open, expanded, canEditFields, isPending]);

    React.useEffect(() => {
        if (open && !wasOpen.current) {
            setExpanded(hasSelected);
        }
        wasOpen.current = open;
    }, [open, hasSelected]);

    const groupedTargetMedias = React.useMemo(
        () => groupTargetMedias(targetMediaOptions, getTargetMediaLabel),
        [targetMediaOptions, getTargetMediaLabel]
    );

    const addableMedias = Array.from(new Set(targetMediaOptions))
        .filter(media => media !== effectiveTargetMedia && !options.includes(media));
    const searchTerm = mediaSearch.trim().toLocaleLowerCase('fi');
    const groupedAddableMedias = groupTargetMedias(
        addableMedias.filter(media => getTargetMediaLabel(media).toLocaleLowerCase('fi').includes(searchTerm)),
        getTargetMediaLabel
    );

    const disabled = !canEditFields || isPending;
    const setSelection = value => setArrayField(editableFields.MULTI_PUBLICATION_TARGET_MEDIAS, value);

    return (
        <Stack spacing={1} sx={{ mt: 2, mb: 1, width: '100%', alignItems: 'flex-start', textAlign: 'left' }}>
            <TextField
                select
                fullWidth
                size="small"
                label="Pääjulkaisu"
                value={effectiveTargetMedia || ''}
                disabled={disabled || targetMediaOptions.length === 0}
                onChange={event => onTargetMediaChange(event.target.value)}
            >
                {groupedTargetMedias.flatMap(([letter, medias]) => [
                    <LetterSubheader key={`group-${letter}`}>{letter}</LetterSubheader>,
                    ...medias.map(({ media, label }) => (
                        <MenuItem key={media} value={media}>{label}</MenuItem>
                    )),
                ])}
            </TextField>
            <Accordion
                expanded={expanded}
                onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
                disableGutters
                elevation={0}
                sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    bgcolor: 'transparent',
                    backgroundImage: 'none',
                    border: 1,
                    borderColor: theme => alpha(theme.palette.text.primary, 0.23),
                    '&:hover': { borderColor: 'text.primary' },
                    borderRadius: 1,
                    overflow: 'hidden',
                    '&:first-of-type, &:last-of-type': { borderRadius: 1 },
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1">
                        Alajulkaisu{selected.length > 0 ? ` (${selected.length} valittu)` : ''}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 0.5, width: '100%' }}>
                        {options.map(media => (
                            <FormControlLabel
                                key={media}
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selected.includes(media)}
                                        disabled={disabled}
                                        onChange={event => setSelection(event.target.checked
                                            ? [...selected, media]
                                            : selected.filter(value => value !== media))}
                                    />
                                }
                                label={getTargetMediaLabel(media)}
                            />
                        ))}
                    </Box>
                    {options.length > 0 && <Divider sx={{ my: 1 }} />}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            disabled={disabled || addableMedias.length === 0}
                            onClick={event => {
                                setMediaSearch('');
                                setAddMediaAnchor(event.currentTarget);
                            }}
                        >
                            Lisää media
                        </Button>
                        {options.length > 0 && (
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selected.length === options.length}
                                        indeterminate={selected.length > 0 && selected.length < options.length}
                                        disabled={disabled}
                                        onChange={event => setSelection(event.target.checked ? options : [])}
                                    />
                                }
                                label="Valitse kaikki"
                            />
                        )}
                    </Box>
                    <Popover
                        open={Boolean(addMediaAnchor)}
                        anchorEl={addMediaAnchor}
                        onClose={() => setAddMediaAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{ sx: { width: 300, maxWidth: 'calc(100vw - 32px)' } }}
                    >
                        <Box sx={{ p: 1 }}>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                label="Hae mediaa"
                                value={mediaSearch}
                                onChange={event => setMediaSearch(event.target.value)}
                            />
                        </Box>
                        <MenuList disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }} aria-label="Lisää media">
                            {groupedAddableMedias.flatMap(([letter, medias]) => [
                                <ListSubheader key={`add-group-${letter}`}>{letter}</ListSubheader>,
                                ...medias.map(({ media, label }) => (
                                    <MenuItem
                                        key={media}
                                        disabled={disabled}
                                        onClick={() => {
                                            setSelection([...selected, media]);
                                            setAddMediaAnchor(null);
                                            setMediaSearch('');
                                        }}
                                    >
                                        {label}
                                    </MenuItem>
                                )),
                            ])}
                            {groupedAddableMedias.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Ei hakutuloksia</Typography>
                            )}
                        </MenuList>
                    </Popover>
                </AccordionDetails>
            </Accordion>
        </Stack>
    );
}
